import { useEffect, useMemo, useState } from "react";
import { format, isSameDay, subDays } from "date-fns";
import { AlertCircle, ImageOff, MessageSquare, SearchX } from "lucide-react";
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  FilterMenu,
  Kpi,
  Page,
  PageHeader,
  SidePanel,
  Toolbar,
  type DataTableColumn,
  type FilterOption,
} from "@/components/ds";
import { usePageCrumbs } from "@/components/layout/breadcrumbs";
import { useAppFeedback, useAppFeedbackSummary } from "@/hooks/useAppFeedback";
import { useToast } from "@/hooks/use-toast";
import type { AppFeedbackResponse } from "@/types/generated/cms.generated";

/** The KPIs always cover the last 30 days, whatever the Period filter. */
const KPI_DAYS = 30;

const STAR_OPTIONS: FilterOption[] = [
  { value: "all", label: "All" },
  { value: "5", label: "5 ★" },
  { value: "4", label: "4 ★" },
  { value: "3", label: "3 ★" },
  { value: "2", label: "2 ★" },
  { value: "1", label: "1 ★" },
  { value: "none", label: "No rating" },
];

/** `AppBrand` carries exactly these labels (ps-feedback-support-server `AppBrandResolver`). */
const APP_OPTIONS: FilterOption[] = [
  { value: "all", label: "All" },
  { value: "Athlyn app", label: "Athlyn app" },
  { value: "Amplify+", label: "Amplify+" },
];

const PERIOD_OPTIONS: FilterOption[] = [
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
  { value: "365", label: "Last 12 months" },
];

const time = (row: AppFeedbackResponse) => (row.createdAt ? new Date(row.createdAt).getTime() : 0);

/** "Today 10:42" / "Yesterday" / "8 Oct", in the browser's local time. */
export function feedbackDateLabel(createdAt: string | undefined, now: Date = new Date()): string {
  if (!createdAt) return "–";
  const date = new Date(createdAt);
  if (isSameDay(date, now)) return `Today ${format(date, "HH:mm")}`;
  if (isSameDay(date, subDays(now, 1))) return "Yesterday";
  return format(date, date.getFullYear() === now.getFullYear() ? "d MMM" : "d MMM yyyy");
}

/** Joins the parts that are present ("2.9.0 · iPhone 15"). */
const joinPresent = (parts: Array<string | null | undefined>, sep: string) =>
  parts.filter((p): p is string => !!p && p.trim().length > 0).join(sep);

interface StarsProps {
  rating: number | null | undefined;
}

function Stars({ rating }: StarsProps) {
  if (rating === null || rating === undefined) return <span className="text-ds-ink3">No rating</span>;
  const filled = Math.max(0, Math.min(5, rating));
  return (
    <span className="whitespace-nowrap tracking-[1px]" aria-label={`${filled} of 5 stars`} role="img">
      <span className="text-ds-warn">{"★".repeat(filled)}</span>
      <span className="text-ds-line2">{"★".repeat(5 - filled)}</span>
    </span>
  );
}

/** App feedback — what employees tell us about the app (platform-wide; Athlyn staff only). */
export default function AppFeedbackPage() {
  usePageCrumbs([{ label: "App feedback" }]);
  const { toast } = useToast();
  const [days, setDays] = useState(KPI_DAYS);
  const [search, setSearch] = useState("");
  const [stars, setStars] = useState("all");
  const [app, setApp] = useState("all");
  const [company, setCompany] = useState("all");
  const [selected, setSelected] = useState<AppFeedbackResponse | null>(null);

  const list = useAppFeedback(days);
  const summary = useAppFeedbackSummary(KPI_DAYS);

  const rows = useMemo(() => [...(list.data ?? [])].sort((a, b) => time(b) - time(a)), [list.data]);

  // Keyed on the organisation id (two companies may share a name); an empty name reads "—".
  const companyOptions = useMemo<FilterOption[]>(() => {
    const byId = new Map<string, string>();
    for (const r of rows) {
      if (r.organizationId && !byId.has(r.organizationId)) byId.set(r.organizationId, r.organizationName?.trim() || "—");
    }
    const options = Array.from(byId, ([value, label]) => ({ value, label }));
    options.sort((a, b) => a.label.localeCompare(b.label));
    return [{ value: "all", label: "All" }, ...options];
  }, [rows]);

  // A Period change can drop the selected company from the list: fall back to All.
  useEffect(() => {
    if (list.isSuccess && company !== "all" && !companyOptions.some((o) => o.value === company)) setCompany("all");
  }, [list.isSuccess, company, companyOptions]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (stars === "none" ? r.rating != null : stars !== "all" && r.rating !== Number(stars)) return false;
      if (app !== "all" && r.appBrand !== app) return false;
      if (company !== "all" && r.organizationId !== company) return false;
      if (!q) return true;
      return [r.comment, r.userFullName, r.organizationName, r.userEmail].some((v) => v?.toLowerCase().includes(q));
    });
  }, [rows, search, stars, app, company]);

  // "Without a rating" counts the loaded rows of the last 30 days (a 90/365-day list is a superset).
  const lastThirty = useMemo(() => {
    const since = subDays(new Date(), KPI_DAYS).getTime();
    return rows.filter((r) => time(r) >= since);
  }, [rows]);
  const unrated = lastThirty.filter((r) => r.rating == null).length;

  const clearFilters = () => {
    setSearch("");
    setStars("all");
    setApp("all");
    setCompany("all");
  };

  const copyEmail = async (email: string) => {
    try {
      await navigator.clipboard.writeText(email);
      toast({ title: "Email copied" });
    } catch {
      toast({ title: "Couldn't copy the email", variant: "destructive" });
    }
  };

  const columns: DataTableColumn<AppFeedbackResponse>[] = [
    { id: "date", header: "Date", cell: (r) => <span className="whitespace-nowrap">{feedbackDateLabel(r.createdAt)}</span> },
    { id: "company", header: "Company", cell: (r) => <span className="whitespace-nowrap">{r.organizationName}</span> },
    {
      id: "person",
      header: "Person",
      cell: (r) => <span className="whitespace-nowrap font-semibold text-ds-ink">{r.userFullName}</span>,
    },
    { id: "rating", header: "Rating", cell: (r) => <Stars rating={r.rating} /> },
    {
      id: "message",
      header: "Message",
      cell: (r) => <span className="line-clamp-2 min-w-[220px]">{r.comment}</span>,
    },
    {
      id: "app",
      header: "App",
      cell: (r) => {
        const detail = joinPresent([r.appVersion, r.deviceModel], " · ");
        return (
          <span className="whitespace-nowrap">
            {r.appBrand}
            {detail && <span className="block text-xs text-ds-ink3">{detail}</span>}
          </span>
        );
      },
    },
  ];

  const average = summary.data?.averageRating;
  const total = filtered.length;

  return (
    <Page data-testid="app-feedback-page">
      <PageHeader
        title="App feedback"
        subtitle="What employees tell us about the app. Every message is also emailed to support@athlyn.app."
      />

      <div className="mb-3.5 grid gap-3 sm:grid-cols-3" data-testid="app-feedback-kpis">
        <Kpi label="Feedback (30 days)" value={summary.data?.count ?? "–"} />
        <Kpi label="Average rating" value={average != null ? `${average.toFixed(1)} ★` : "–"} />
        <Kpi
          label="Without a rating"
          value={list.isSuccess ? unrated : "–"}
          note={lastThirty.length > 0 ? `${Math.round((unrated / lastThirty.length) * 100)}%` : "–"}
          noteTone="muted"
        />
      </div>

      <Card>
        <Toolbar
          search={{ value: search, onChange: setSearch, placeholder: "Search feedback", label: "Search feedback" }}
          filters={
            <>
              <FilterMenu label="Stars" value={stars} options={STAR_OPTIONS} onChange={setStars} />
              <FilterMenu label="App" value={app} options={APP_OPTIONS} onChange={setApp} />
              <FilterMenu label="Company" value={company} options={companyOptions} onChange={setCompany} />
              <FilterMenu label="Period" value={String(days)} options={PERIOD_OPTIONS} onChange={(v) => setDays(Number(v))} />
            </>
          }
          count={list.isSuccess ? `${total} messages` : undefined}
        />
        {list.isError ? (
          <EmptyState
            icon={AlertCircle}
            title="Couldn't load app feedback"
            action={{ label: "Try again", onClick: () => void list.refetch() }}
          />
        ) : (
          <DataTable
            label="App feedback"
            columns={columns}
            rows={filtered}
            getRowId={(r) => r.id ?? `${r.createdAt}-${r.userFullName}`}
            onRowClick={setSelected}
            isLoading={list.isLoading}
            empty={
              rows.length === 0 ? (
                <EmptyState icon={MessageSquare} title="No app feedback yet." />
              ) : (
                <EmptyState icon={SearchX} title="Nothing matches these filters." action={{ label: "Clear filters", onClick: clearFilters }} />
              )
            }
          />
        )}
      </Card>

      <SidePanel
        open={selected !== null}
        onOpenChange={(open) => !open && setSelected(null)}
        title={`Feedback from ${selected?.userFullName ?? ""}`}
      >
        {selected && <FeedbackDetail row={selected} onCopy={copyEmail} />}
      </SidePanel>
    </Page>
  );
}

interface FeedbackDetailProps {
  row: AppFeedbackResponse;
  onCopy: (email: string) => void;
}

function FeedbackDetail({ row, onCopy }: FeedbackDetailProps) {
  const email = row.userEmail?.trim() ?? "";
  return (
    <div data-testid="feedback-detail">
      <Stars rating={row.rating} />
      <p className="my-2.5 whitespace-pre-wrap text-[14px] text-ds-ink">{row.comment}</p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 text-[13px]">
        <dt className="text-ds-ink3">Company</dt>
        <dd className="font-semibold text-ds-ink">{row.organizationName}</dd>
        {email && (
          <>
            <dt className="text-ds-ink3">Email</dt>
            <dd className="flex flex-wrap items-center gap-1.5 font-semibold text-ds-ink">
              <span className="break-all">{email}</span>
              <Button size="sm" variant="ghost" onClick={() => onCopy(email)}>
                Copy
              </Button>
            </dd>
          </>
        )}
        <dt className="text-ds-ink3">App</dt>
        <dd className="font-semibold text-ds-ink">{joinPresent([row.appBrand, row.appVersion], " ") || "–"}</dd>
        <dt className="text-ds-ink3">Device</dt>
        <dd className="font-semibold text-ds-ink">{joinPresent([row.deviceModel, row.platform], " · ") || "–"}</dd>
        <dt className="text-ds-ink3">Sent</dt>
        <dd className="font-semibold text-ds-ink">
          {row.createdAt ? format(new Date(row.createdAt), "d MMM yyyy, HH:mm") : "–"}
        </dd>
      </dl>
      {row.screenshotUrl ? (
        <a
          href={row.screenshotUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 block overflow-hidden rounded-ds border border-ds-line focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-accent"
          aria-label="Open the screenshot full size"
        >
          <img src={row.screenshotUrl} alt="Screenshot sent with the feedback" className="block max-h-[360px] w-full object-contain bg-ds-sunk" />
        </a>
      ) : (
        <div className="mt-3 grid h-[120px] place-items-center rounded-ds border border-dashed border-ds-line2 bg-ds-sunk text-ds-ink3">
          <span className="flex flex-col items-center gap-1 text-xs">
            <ImageOff className="h-5 w-5" aria-hidden="true" />
            No screenshot
          </span>
        </div>
      )}
    </div>
  );
}
